import { KeyboardEvent, PointerEvent, useRef } from 'react';
import {
  CHATS_LIST_MAX_WIDTH,
  CHATS_LIST_MIN_WIDTH,
  resetChatsListWidth,
  setChatsListWidth,
} from '../../../lib/use-chats-list-width';
import styles from './ChatsListResizeHandle.module.css';

/**
 * The grab strip on the Chats split view's list/conversation boundary — the SAME
 * boundary `FloatingChat/ChatResizeHandle.tsx` grabs elsewhere in the app, mirrored
 * for the list side of a different store (`use-chats-list-width.ts`). Dragging
 * toward the conversation shrinks the list (and grows the transcript, which gets
 * "the rest" via `CHATS_DOCK_WIDTH`); toward the list does the opposite. Read the
 * element's own computed direction so a drag/arrow-key means the same thing under
 * either writing direction. Drag, arrow keys (Shift = bigger step), or double-click
 * to reset. Rendered only in `split` — the `stacked` layout has nothing to split.
 */
function isRtl(el: Element): boolean {
  return getComputedStyle(el).direction === 'rtl';
}

export function ChatsListResizeHandle({ width, label, onDraggingChange }: {
  width: number; label: string; onDraggingChange: (dragging: boolean) => void;
}) {
  const drag = useRef<{ x: number; w: number; rtl: boolean } | null>(null);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, w: width, rtl: isRtl(e.currentTarget) };
    onDraggingChange(true);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const s = drag.current;
    if (!s) return;
    const delta = e.clientX - s.x;
    // The handle sits at the list's OWN inline-end edge, so dragging it toward the inline-end
    // (LTR: rightward) grows the list — the opposite sign from the chat dock's handle, which
    // sits at the chat's inline-START edge.
    setChatsListWidth(s.w + (s.rtl ? -delta : delta));
  };

  const end = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    onDraggingChange(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer already released
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const rtl = isRtl(e.currentTarget);
    const step = (e.shiftKey ? 48 : 16) * (rtl ? -1 : 1);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setChatsListWidth(width - step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setChatsListWidth(width + step);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      resetChatsListWidth();
    }
  };

  return (
    <div
      className={styles.handle}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={CHATS_LIST_MIN_WIDTH}
      aria-valuemax={CHATS_LIST_MAX_WIDTH}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => resetChatsListWidth()}
      onKeyDown={onKeyDown}
    >
      <span className={styles.grip} aria-hidden />
    </div>
  );
}
